// notificationBars.js
import cookies from 'js-cookie';
import api from '../marketing/api';
import { select } from './utils/dom';

// One site-wide notificaiton is available at /nisource-api/ldc/notification on the LDC sites.
// So we'll query for a the notification every page load, don't cache it, and allow it to be
// dismissed (removed from the current page).
// Also allow a cookie to be set to see if the user has already dismissed a specific warning
// so it doesn't load again when navigating back to the site.
// For local development, see the nisource-api-spec repo and marketing.yml.
const notificationCookieName = 'notificationHasBeenDismissed';
const notificationCookie = cookies.getJSON(notificationCookieName);
const appNotificationCookieName = 'appNotificationHasBeenDismissed';
const appNotificationCookie = cookies.get(appNotificationCookieName);
let prevScrollpos = window.pageYOffset;
let showAppNotification = false;

/**
 * Helper function
 * @param {string} id notification id from the response data
 */
function hasNotificationBeenClosed(id) {
  // return false if the ID of the notification matches and ID of a notification that has already been closed (so don't show it)
  // or return true to show the notification!
  if (notificationCookie && id === notificationCookie.notificationId) {
    return true;
  }
  return false;
}
function hasAppNotificationBeenClosed() {
  if (appNotificationCookie && appNotificationCookie === 'true') {
    return true;
  }
  return false;
}

/**
 * Notification bar class.
 */
class Notification {
  /**
   * Create a notification bar.
   * @param {object} notification
   * @param {string} notification.backgroundColor
   * @param {string} notification.ctaButtonText
   * @param {string} notification.ctaLinkUrl
   * @param {string} notification.description
   * @param {string} notification.icon
   * @param {string} notification.textColor
   * @param {string} notification.title
   * @param {string} notification.id
   * @param {boolean} notification.hideTitle
   * @param {string} notification.textAlign
   * @param {boolean} notification.hideCloseButton
   * @param {HTMLElement} container place for the notification to be injected.
   */
  constructor(notification, container) {
    // start DOM array
    this.DOM = {
      container,
      desktopHeader: select('.SiteHeader--desktop'),
      mobileHeader: select('.SiteHeader--mobile'),
      notificationArea: select('.notification-area'),
      googleTranslate: select('.SiteHeader--googleTranslate'),
      main: select('main'),
      htmlTag: select('html'),
      notificationBar: select('.notification-container'),
      appNotificationBar: select('.app-notification-container'),
    };

    // save notification ID so we can access it later when setting cookies for closed notifications
    this.id = notification.id;

    this.DOM.container.classList.add(`notification-container--bg-${notification.backgroundColor}`);

    // generate the HTML for the notification
    let template = `
      <div id="${notification.id}" class="notification-bar container notification-bar--text-${notification.textColor}" tabIndex="-1">
    `;
    if (notification.icon !== "no") {
      template += `
        <div class="notification-bar__icon">
          <svg aria-hidden="true" class="icon icon-${notification.icon} " role="img">
            <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#icon-${notification.icon}"></use>
          </svg>
        </div>`;
    }
    template += `
      <div class="notification-bar__message text-${notification.textAlign}">`;
    if (!notification.hideTitle) {
      template += `<h2 class="notification-bar__title">${notification.title}</h2>`
    }
    template += `<p class="notification-bar__paragraph text-500 inline-block"><span>${notification.description}</span>`;
    if (notification.ctaButtonText && notification.ctaLinkUrl) {
      template += `
        <span class="notification-bar__cta">
          <a href="${notification.ctaLinkUrl}">${notification.ctaButtonText}</a>
        </span>
      `;
    }
    template += `
        </p></div>`;
    if (!notification.hideCloseButton) {
      template += `
          <button class="ui-none notification-bar__close">
            <svg aria-hidden="true" class="icon icon-close-menu " role="img">
              <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#icon-close-menu"></use>
            </svg>
          </button>`;
    }
    template += `</div>
    `;

    // insert it into DOM
    this.DOM.container.insertAdjacentHTML('beforeend', template);

    // reselect and store a reference to the notification bar
    this.DOM.notification = select('.notification-bar');

    this.show();
  }

  /**
   * Show the notification.
   */
  show() {
    // show with css and to screen readers
    this.DOM.notification.setAttribute('aria-hidden', false);
    this.DOM.notification.classList.add('js-show');

    this.adjustPadding();

    // focus the bar
    this.DOM.notification.focus();

    // bind clicks on "close" button to closing the notification
    const close = select('.notification-bar__close', this.DOM.notification);
    if (close) {
      close.addEventListener('click', this.hide.bind(this));
    }

    window.addEventListener('resize', this.adjustPadding.bind(this));
    window.addEventListener('scroll', this.adjustVisibility.bind(this));

    this.setMutationObserver();
  }

  /**
   * Adjust the visibility of the notification
   */
  adjustVisibility() {
    const currentScrollPos = window.pageYOffset;
    const notificationDisplay = this.DOM.notificationBar.style.visibility;
    if (prevScrollpos > currentScrollPos) {
      if (notificationDisplay === 'hidden') {
        if (currentScrollPos < prevScrollpos - 200 || currentScrollPos === 0) {
          this.DOM.notificationBar.style.visibility = 'visible';
          this.adjustPadding();
          prevScrollpos = currentScrollPos;
        }
      } else {
        prevScrollpos = currentScrollPos;
      }
    } else if (!notificationDisplay || notificationDisplay === 'visible') {
      if (currentScrollPos > prevScrollpos + 200) {
        this.DOM.notificationBar.style.visibility = 'hidden';
        this.adjustPadding();
        prevScrollpos = currentScrollPos;
      }
    } else {
      prevScrollpos = currentScrollPos;
    }
  }

  /**
   * Adjust the padding of header and main content
   */
  adjustPadding() {
    let appNotificationHeight = 0;
    if (this.DOM.appNotificationBar && this.DOM.appNotificationBar.style.visibility !== 'hidden') {
      appNotificationHeight = this.DOM.appNotificationBar.getBoundingClientRect().height;
    }
    let notificationHeight = 0;
    if (this.DOM.notificationBar && this.DOM.notificationBar.style.visibility !== 'hidden') {
      notificationHeight = this.DOM.notificationBar.getBoundingClientRect().height;
    }
    if (this.DOM.notificationArea) {
      this.DOM.notificationArea.style.height = `${notificationHeight}px`;
    }
    let googleTranslateHeight = 0;
    let googleTranlationBar = 0;

    if (this.DOM.googleTranslate) {
      googleTranslateHeight = 46;
      const cookieVal = cookies.getJSON('googtrans');

      if (cookieVal) {
        googleTranlationBar = 40;
        googleTranslateHeight += googleTranlationBar;
      }

      this.DOM.googleTranslate.style.top = `${googleTranlationBar + appNotificationHeight + notificationHeight}px`;
    }
    if (this.DOM.appNotificationBar) {
      this.DOM.appNotificationBar.style.top = `${googleTranlationBar}px`;
    }
    this.DOM.notificationBar.style.top = `${googleTranlationBar + appNotificationHeight}px`;

    if (this.DOM.desktopHeader && this.DOM.mobileHeader) {
      this.DOM.desktopHeader.style.top = `${googleTranslateHeight + appNotificationHeight + notificationHeight}px`;
      this.DOM.mobileHeader.style.top = `${googleTranslateHeight + appNotificationHeight + notificationHeight}px`;

      // if there's a main element, move that down too
      if (this.DOM.main) {
        let headerHeight = this.DOM.desktopHeader.clientHeight;

        if (this.DOM.desktopHeader.clientHeight === 0) {
          headerHeight = this.DOM.mobileHeader.clientHeight;
        }

        this.DOM.main.style.paddingTop = `${(googleTranslateHeight + headerHeight + appNotificationHeight + notificationHeight)}px`;
      }
    }
  }

  setMutationObserver() {
    const self = this;
    const opts = { attributes: true };
    const htmlTagObserver = new MutationObserver(() => {
      self.adjustPadding();
    });

    htmlTagObserver.observe(this.DOM.htmlTag, opts);
  }

  /**
   * Hide the notification.
   */
  hide() {
    // hide with css and to screen readers
    this.DOM.notification.setAttribute('aria-hidden', true);
    this.DOM.notification.classList.remove('js-show');

    this.adjustPadding();
    window.removeEventListener('resize', this.adjustPadding.bind(this));

    // set a cookie, which expires in 7 days, with the response to avoid subsequent calls
    // https://github.com/js-cookie/js-cookie/wiki/Frequently-Asked-Questions#expire-cookies-in-less-than-a-day
    cookies.set(notificationCookieName, {
      // put in the notification ID when it exists
      notificationId: this.id,
      hasBeenClosed: true,
    });
  }
}

// the place we'll put the site-wide notificaiton.
const container = select('.notification-container');
if (container) {
  // check for notifications.
  api.get('/notification', {}, false).then(response => {
    if ('data' in response && response.data.length > 0) {
      const notification = response.data[0];
      // check the notification ID from the response from the server against any potential IDs that have already been closed
      if (!hasNotificationBeenClosed(notification.id)) {
        // only show first one.
        new Notification(notification, container); // eslint-disable-line no-new
      }
    }
  }).catch(err => {
    console.warn('Notification.js', err);
  });
}

class AppNotification {
  /**
   * Create a notification bar.
   * @param {object} notification
   * @param {string} notification.ctaButtonText
   * @param {string} notification.ctaAndroidLink
   * @param {string} notification.ctaIOSLink
   * @param {string} notification.ctaLink
   * @param {string} notification.description
   * @param {string} notification.imageURL
   * @param {string} notification.title
   * @param {HTMLElement} appNotificationContainer place for the notification to be injected.
   */
  constructor(notification, appNotificationContainer) {
    // start DOM array
    this.DOM = {
      appNotificationContainer,
      desktopHeader: select('.SiteHeader--desktop'),
      mobileHeader: select('.SiteHeader--mobile'),
      logoOnlyHeader: select('.SiteHeader--only-logo'),
      notificationArea: select('.notification-area'),
      googleTranslate: select('.SiteHeader--googleTranslate'),
      main: select('main'),
      htmlTag: select('html'),
      notificationBar: select('.notification-container'),
      appNotificationBar: select('.app-notification-container'),
    };

    // save notification ID so we can access it later when setting cookies for closed notifications
    this.id = notification.id;

    // generate the HTML for the notification
    let template = `
      <div class="app-notification-bar container" tabIndex="-1">
    `;
    template += `
        <button class="ui-none app-notification-bar__close">
          <svg aria-hidden="true" class="icon icon-close-menu " role="img">
            <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#icon-close-menu"></use>
          </svg>
        </button>`;
    template += `
        <img class="ui-none app-notification-bar__img" src=${notification.imageURL}>`;
    template += `
      <div class="app-notification-bar__message">`;
    template += `<h2 class="app-notification-bar__title">${notification.title}</h2>`
    template += `<p class="app-notification-bar__paragraph text-500 inline-block"><span>${notification.description}</span>`;
    template += `
        </p></div>`;
    if (notification.ctaButtonText && notification.ctaLink) {
      template += `
        <span class="app-notification-bar__cta">
          <a href="${notification.ctaLink}">${notification.ctaButtonText}</a>
        </span>
      `;
    }
    template += `</div>
    `;

    // insert it into DOM
    this.DOM.appNotificationContainer.insertAdjacentHTML('beforeend', template);

    // reselect and store a reference to the notification bar
    this.DOM.appNotification = select('.app-notification-bar');

    this.show();
  }

  /**
   * Show the notification.
   */
  show() {
    // show with css and to screen readers
    this.DOM.appNotification.setAttribute('aria-hidden', false);
    this.DOM.appNotification.classList.add('js-show');

    this.adjustPadding();

    // focus the bar
    this.DOM.appNotification.focus();

    // bind clicks on "close" button to closing the notification
    const close = select('.app-notification-bar__close', this.DOM.appNotification);
    if (close) {
      close.addEventListener('click', this.hide.bind(this));
    }

    window.addEventListener('resize', this.adjustPadding.bind(this));

    this.setMutationObserver();
  }

  /**
   * Adjust the padding of header and main content
   */
  adjustPadding() {
    let appNotificationHeight = 0;
    if (this.DOM.appNotificationBar && this.DOM.appNotificationBar.style.visibility !== 'hidden') {
      appNotificationHeight = this.DOM.appNotificationBar.getBoundingClientRect().height;
    }
    let notificationHeight = 0;
    if (this.DOM.notificationBar && this.DOM.notificationBar.style.visibility !== 'hidden') {
      notificationHeight = this.DOM.notificationBar.getBoundingClientRect().height;
    }
    let googleTranslateHeight = 0;
    let googleTranlationBar = 0;

    if (this.DOM.googleTranslate) {
      googleTranslateHeight = 46;
      const cookieVal = cookies.getJSON('googtrans');

      if (cookieVal) {
        googleTranlationBar = 40;
        googleTranslateHeight += googleTranlationBar;
      }

      this.DOM.googleTranslate.style.top = `${googleTranlationBar + appNotificationHeight + notificationHeight}px`;
    }

    this.DOM.appNotificationBar.style.top = `${googleTranlationBar}px`;

    if (this.DOM.notificationBar) {
      this.DOM.notificationBar.style.top = `${googleTranlationBar + appNotificationHeight}px`;
    }

    if (this.DOM.desktopHeader && this.DOM.mobileHeader) {
      this.DOM.desktopHeader.style.top = `${googleTranslateHeight + appNotificationHeight + notificationHeight}px`;
      this.DOM.mobileHeader.style.top = `${googleTranslateHeight + appNotificationHeight + notificationHeight}px`;
    }

    if (this.DOM.logoOnlyHeader) {
      this.DOM.logoOnlyHeader.style.paddingTop = `${googleTranslateHeight + appNotificationHeight + notificationHeight}px`;
    }

    // if there's a main element, move that down too
    if (this.DOM.main) {
      let headerHeight = 0;

      if (this.DOM.desktopHeader) {
        headerHeight = this.DOM.desktopHeader.clientHeight;
      }
      if (this.DOM.mobileHeader && this.DOM.desktopHeader.clientHeight === 0) {
        headerHeight = this.DOM.mobileHeader.clientHeight;
      }

      this.DOM.main.style.paddingTop = `${(googleTranslateHeight + headerHeight + appNotificationHeight + notificationHeight)}px`;
    }
  }

  setMutationObserver() {
    const self = this;
    const opts = { attributes: true };
    const htmlTagObserver = new MutationObserver(() => {
      self.adjustPadding();
    });

    htmlTagObserver.observe(this.DOM.htmlTag, opts);
  }

  /**
   * Hide the notification.
   */
  hide() {
    // hide with css and to screen readers
    this.DOM.appNotification.setAttribute('aria-hidden', true);
    this.DOM.appNotification.classList.remove('js-show');

    this.adjustPadding();

    window.removeEventListener('resize', this.adjustPadding.bind(this));

    // set a cookie, which expires in 7 days, with the response to avoid subsequent calls
    // https://github.com/js-cookie/js-cookie/wiki/Frequently-Asked-Questions#expire-cookies-in-less-than-a-day
    cookies.set(appNotificationCookieName, "true");
  }
}
const appNotificationContainer = select('.app-notification-container');
if (appNotificationContainer) {
  if (!hasAppNotificationBeenClosed()) {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    let isAndroid = false;
    if (/windows phone/i.test(userAgent)) {
      showAppNotification = false;
    }

    if (/android/i.test(userAgent)) {
      showAppNotification = true;
      isAndroid = true;
    }

    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      showAppNotification = true;
      const isSafari = navigator.vendor && navigator.vendor.match('Apple') &&
               userAgent &&
               (!userAgent.match('CriOS') &&
               !userAgent.match('FxiOS') &&
               userAgent.match('Safari'));
      if (isSafari) {
        showAppNotification = false;
      }
    }
    if (showAppNotification) {
      api.get('/appbannernotification', {}, false).then(response => {
        if (response.data && response.data.enableAppBanner === true) {
          const notification = response.data;
          if (isAndroid) {
            notification.ctaLink = notification.ctaAndroidLink;
          } else {
            notification.ctaLink = notification.ctaIOSLink;
          }
          new AppNotification(notification, appNotificationContainer); // eslint-disable-line no-new
        }
      }).catch(err => {
        console.warn('Notification.js', err);
      });
    }
  }
}



// WEBPACK FOOTER //
// ./src/assets/toolkit/scripts/shared/notification.js